import {
	AttachmentStatus,
	AttachmentType,
	FileAttachment,
	type Attachment,
} from '@/utils/fetchers/attachment'
import { VIDEO_UPLOAD_LIMIT_QUERY_KEY, VideoUploadLimit } from '@/utils/fetchers/manifest'
import { getVideoMetadata } from '@/utils/media'
import queryClient from '@/utils/queryClient'
import { isNumber } from 'lodash-es'
import { z } from 'zod'

// 视频校验函数
export async function validateVideo(attachment: FileAttachment, ctx: z.RefinementCtx) {
	if (!attachment.file) {
		return
	}

	const limit = queryClient.getQueryData<VideoUploadLimit>(VIDEO_UPLOAD_LIMIT_QUERY_KEY) ?? {
		maxDuration: 360,
		maxSize: 100,
	}
	const params = {
		type: 'ONLY_AVAILABLE_IN_JIKE_APP',
	}

	// 检查视频格式
	if (!attachment.file.name.toLowerCase().endsWith('.mp4')) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '仅支持 MP4 格式的视频',
			params,
		})
	}

	// 检查视频大小
	if (attachment.file.size > limit.maxSize * 1024 * 1024) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: `视频大小不能超过 ${limit.maxSize}MB`,
			params,
		})
	}

	const metadata = await getVideoMetadata(attachment.file)
	// 检查视频分辨率
	if (metadata.width && metadata.height) {
		const minSide = Math.min(metadata.width, metadata.height)
		const maxSide = Math.max(metadata.width, metadata.height)

		if (minSide > 1080 || maxSide > 1920) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频分辨率不能超过 1920x1080',
				params,
			})
		}
	}

	// 检查视频时长
	if (metadata.duration) {
		if (metadata.duration < 3) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频时长不能少于 3 秒',
			})
		} else if (metadata.duration > limit.maxDuration) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `视频时长不能超过 ${limit.maxDuration}秒`,
			})
		}
	}
}

export const AttachmentSchema = z
	.custom<Attachment>()
	.refine(
		(attachment) => {
			if (attachment.type === AttachmentType.PICTURE) {
				const file = (attachment as FileAttachment).file
				if (isNumber(file?.size)) {
					return file.size <= 20 * 1024 * 1024
				}
			}
			return true
		},
		{
			message: '图片体积不能超过 20MB',
		}
	)
	.superRefine(async (attachment, ctx) => {
		if (attachment.type === AttachmentType.VIDEO) {
			await validateVideo(attachment as FileAttachment, ctx)
		}
	})

export const AttachmentsSchema = z
	.array(AttachmentSchema)
	.refine(
		(attachments) => {
			if (
				attachments.filter((item) => item.type === AttachmentType.PICTURE).length > 0 &&
				attachments.filter((item) => item.type === AttachmentType.VIDEO).length > 0
			) {
				return false
			}
			return true
		},
		{
			message: '不能同时添加图片和视频',
		}
	)
	.refine(
		(attachments) => {
			const pictures = attachments.filter((item) => item.type === AttachmentType.PICTURE)
			return pictures.length <= 9
		},
		{
			message: '最多添加9张图片',
		}
	)
	.refine(
		(attachments) => {
			return attachments.filter((item) => item.type === AttachmentType.VIDEO).length <= 1
		},
		{
			message: '最多添加1个视频',
		}
	)

export const InPayloadAttachmentsSchema = AttachmentsSchema.refine(
	(attachments) =>
		attachments.every((item) => {
			if (item.type === AttachmentType.LINK) {
				return true
			}
			return (
				[AttachmentType.PICTURE, AttachmentType.VIDEO].includes(item.type) &&
				(item as FileAttachment).status === AttachmentStatus.UPLOADED
			)
		}),
	{
		message: '请等待所有附件上传完成',
	}
)

export const FormAttachmentSchema = z.object({
	ids: z.array(z.string()),
	map: z.record(z.string(), AttachmentSchema),
})

export type FormAttachment = z.infer<typeof FormAttachmentSchema>
